// covered-by: frontends/carrie/src/routes/tickets/routes/components/comment-drawer/__tests__/Comments.test.tsx
import { useCallback, useEffect, useRef } from "react";

import {
  AIReviewFailedError,
  followAIReview,
} from "../../../../../services/projects";
import { errorMessage } from "../../../../../utils/http";

type FollowArgs = Parameters<typeof followAIReview>[0];
type FollowResult = Awaited<ReturnType<typeof followAIReview>>;

export function isAbortError(error: unknown) {
  return (
    typeof error === "object" &&
    error !== null &&
    "name" in error &&
    error.name === "AbortError"
  );
}

/** The AI Reviewer error toast's message for a review that did not finish. */
export async function aiReviewErrorMessage(error: unknown) {
  if (error instanceof AIReviewFailedError) {
    return "The AI review could not finish. Please try again.";
  }
  return errorMessage(error);
}

/**
 * Owns the lifetime of an AI review started from the comment drawer. The
 * foreground poll and the background follow-up share one AbortController per
 * ticket revision, so neither outlives the drawer or the revision it started
 * on, and neither can toast on another page.
 */
export function useAIReviewFollowUp(scopeKey: string) {
  const controllerRef = useRef(new AbortController());

  useEffect(() => {
    const controller = new AbortController();
    controllerRef.current = controller;
    return () => controller.abort();
  }, [scopeKey]);

  const signal = useCallback(() => controllerRef.current.signal, []);

  const follow = useCallback(
    (
      args: FollowArgs,
      handlers: {
        onComplete: (result: FollowResult) => void;
        onFail: (error: unknown) => void;
      },
    ) => {
      const controller = controllerRef.current;
      followAIReview(args, controller.signal).then(
        (result) => {
          if (!controller.signal.aborted) {
            handlers.onComplete(result);
          }
        },
        (error: unknown) => {
          if (!controller.signal.aborted && !isAbortError(error)) {
            handlers.onFail(error);
          }
        },
      );
    },
    [],
  );

  return { signal, follow };
}
